import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import TypeBadge from '../../components/TypeBadge';
import api from '../../api/ApiClient';

function StatCard({ label, value, note, accent }) {
  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || 'var(--text)', fontFamily: 'Space Mono', letterSpacing: '-1px', marginBottom: 4 }}>{value}</div>
      {note && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{note}</div>}
    </div>
  );
}

const STATUS_STYLE = {
  draft:     { color: '#64748B', bg: '#F1F5F9' },
  pending:   { color: '#D97706', bg: '#FFFBEB' },
  verified:  { color: '#1D4ED8', bg: '#EFF6FF' },
  rejected:  { color: '#DC2626', bg: '#FEF2F2' },
  published: { color: '#16A34A', bg: '#F0FDF4' },
};

export default function Overview() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getOrders('bought'),
      api.getMyAssets(),
      api.getSubscriptions(),
    ]).then(([o, a, s]) => {
      setOrders(o.orders || []);
      setAssets(a.assets || []);
      setSubs(s.subscriptions || []);
    }).finally(() => setLoading(false));
  }, []);

  const ss = user?.sellerStats || {};
  const hasSales = (ss.totalSales || 0) > 0;

  if (loading) return <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading…</div>;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px', marginBottom: 24 }}>
        Good day, {user?.name?.split(' ')[0] || 'there'}
      </h1>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 32 }}>
        <StatCard label="Balance" value={`$${(user?.balance || 0).toFixed(2)}`} accent="var(--success)" />
        <StatCard label="Purchases" value={orders.length} note="assets bought" />
        <StatCard label="My Assets" value={assets.length} note="uploaded" />
        <StatCard label="Subscriptions" value={subs.length} note="active" />
        {hasSales && <StatCard label="Total Earned" value={`$${(ss.totalEarned || 0).toFixed(0)}`} note={`${ss.totalSales} sales`} accent="var(--accent)" />}
        {hasSales && <StatCard label="Rating" value={`${(ss.rating || 0).toFixed(1)}`} note={`${ss.reviewCount} reviews`} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: orders.length ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Recent purchases */}
        {orders.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Recent Purchases</h2>
              <Link to="/dashboard/purchases" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View all</Link>
            </div>
            <div className="card" style={{ overflow: 'hidden' }}>
              {orders.slice(0, 5).map((o, i) => {
                const a = o.assetId || {};
                return (
                  <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{a.title || 'Unknown'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(o.createdAt).toLocaleDateString()}</div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'Space Mono' }}>${o.amount}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* My assets */}
        {assets.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>My Assets</h2>
              <Link to="/dashboard/assets" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View all</Link>
            </div>
            <div className="card" style={{ overflow: 'hidden' }}>
              {assets.slice(0, 5).map((a, i) => {
                const st = STATUS_STYLE[a.status] || STATUS_STYLE.draft;
                return (
                  <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                    <TypeBadge type={a.assetType} size="sm" />
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{a.stats?.views || 0} views</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600, color: st.color, background: st.bg }}>{a.status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
